import { ReactNode, useCallback } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { SelectUser } from "../types/backend";
import { APIError } from "../api";
import { authenticatedUser, logoutUser } from "../api/auth";
import type { AuthContextType } from "./auth-types";
import { AuthContext } from "./auth-context";

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();

  const {
    data: user,
    isLoading,
    isError,
    error,
  } = useQuery<SelectUser | null, APIError>({
    queryKey: ["user"],
    queryFn: async () => {
      console.log("Fetching user data...");
      try {
        const data = await authenticatedUser();
        console.log("User data fetched:", data);
        
        // Check if user has admin role (is_superuser)
        if (data && !data.is_superuser) {
          throw new APIError(403, "FORBIDDEN", new Error("Admin access required"));
        }
        
        return data;
      } catch (err) {
        console.error("Error fetching user:", err);
        if (err instanceof APIError && (err.status === 401 || err.status === 403)) {
          return null;
        }
        throw err;
      }
    },
    retry: (failureCount, error) => {
      console.log("Query failed, attempt:", failureCount, "error:", error);
      // Don't retry on auth errors or server errors
      if (error instanceof APIError && (error.status === 401 || error.status === 403 || error.status >= 500)) {
        return false;
      }
      return failureCount < 3;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    gcTime: 1000 * 60 * 30, // Keep in cache for 30 minutes
  });

  const { mutate: logout, isPending: isLoggingOut } = useMutation<
    { message: string },
    Error,
    void,
    unknown
  >({
    mutationFn: logoutUser,
    onSuccess: () => {
      // Clear all queries to force a clean state
      queryClient.clear();
      // Set authenticated state to false immediately 
      queryClient.setQueryData(["user"], null);
      // Navigate to login page
      window.location.href = "/login";
    },
  });

  // Global auth error handler that can be called from anywhere in the app
  const handleAuthError = useCallback((error: unknown) => {
    console.log("Handling auth error:", error);
    if (error instanceof APIError && (error.status === 401 || error.status === 403)) {
      console.log("Auth error detected, clearing auth state and redirecting to login");
      // Clear all queries to force a clean state
      queryClient.clear();
      // Set authenticated state to false immediately 
      queryClient.setQueryData(["user"], null);
      // Navigate to login page
      window.location.href = "/login";
    }
  }, [queryClient]);

  // Don't automatically logout on errors - let the user retry
  if (isError && error instanceof APIError && (error.status === 401 || error.status === 403)) {
    console.error("Auth error - user not authenticated:", error);
    // Don't call handleAuthError here as it would cause infinite loops
  }

  const isAuthenticated = !!user && user.is_superuser;
  console.log("Auth state:", { isAuthenticated, isLoading, user });

  const value: AuthContextType = {
    logout,
    user,
    isLoading,
    isLoggingOut,
    isAuthenticated,
    handleAuthError,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};